/**
 * Scans a workspace's `.claude/skills/<name>/SKILL.md` folders so per-project
 * Claude-authored skills can surface in the composer of providers that don't
 * natively discover that layout (Codex looks in `.codex/skills`, OpenCode
 * delegates to its own command discovery).
 *
 * Returns lightweight slash-command descriptors `{ name, description }`. This is
 * a LISTING aid only — it makes the skill visible in the `/` menu. Whether the
 * target provider can actually *execute* a `.claude/skills` skill is a separate
 * concern handled per provider.
 */

import { execSync } from "node:child_process";
import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import path from "node:path";

export interface ProjectSkillCommand {
  name: string;
  description: string;
}

function parseSkillFrontMatter(markdown: string): Record<string, string> {
  const lines = markdown.split("\n");
  if (lines[0]?.trim() !== "---") return {};
  let end = -1;
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i]?.trim() === "---") {
      end = i;
      break;
    }
  }
  if (end === -1) return {};
  const frontMatter: Record<string, string> = {};
  for (const line of lines.slice(1, end)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const idx = trimmed.indexOf(":");
    if (idx <= 0) continue;
    const key = trimmed.slice(0, idx).trim();
    const value = trimmed
      .slice(idx + 1)
      .trim()
      .replace(/^['"]/, "")
      .replace(/['"]$/, "");
    if (key && value) frontMatter[key] = value;
  }
  return frontMatter;
}

/**
 * Scan `<cwd>/.claude/skills`, the repo root's `.claude/skills`, and the parent
 * dir's `.claude/skills` for skill folders. Deduplicated by skill name.
 */
export async function scanProjectClaudeSkills(cwd: string): Promise<ProjectSkillCommand[]> {
  const candidates: string[] = [path.join(cwd, ".claude", "skills")];

  const repoRoot = (() => {
    try {
      const output = execSync("git rev-parse --show-toplevel", {
        cwd,
        encoding: "utf8",
        stdio: ["ignore", "pipe", "ignore"],
      });
      const trimmed = output.trim();
      return trimmed ? trimmed : null;
    } catch {
      return null;
    }
  })();
  if (repoRoot) {
    candidates.push(path.join(path.dirname(cwd), ".claude", "skills"));
    candidates.push(path.join(repoRoot, ".claude", "skills"));
  }

  const byName = new Map<string, ProjectSkillCommand>();
  for (const dir of candidates) {
    let entries: Dirent[];
    try {
      entries = await fs.readdir(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const entry of entries) {
      if (!entry.isDirectory() && !entry.isSymbolicLink()) continue;
      const skillPath = path.join(dir, entry.name, "SKILL.md");
      let content: string;
      try {
        content = await fs.readFile(skillPath, "utf8");
      } catch {
        continue;
      }
      const frontMatter = parseSkillFrontMatter(content);
      const name = frontMatter["name"];
      const description = frontMatter["description"];
      if (!name || !description) continue;
      if (!byName.has(name)) byName.set(name, { name, description });
    }
  }

  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}
